import axios, { AxiosResponse } from 'axios';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import { AnyAction } from 'redux';
import { all, delay, put, select, takeEvery } from 'redux-saga/effects';

import { MINIMAL_API_PROMISE_RESOLVE_TIME } from '@/constants/global';
import { BrowserStorageService } from '@/services/BrowserStorage';
import { setGlobalError } from '@/store/reducers/global/actions';
import { ReduxSagaAction } from '@/types';

function* apiRequestHandler(action: ReduxSagaAction) {
  const { sagaPayload, promise, selector, onFail, onSuccess } = action;
  const { FAIL, SUCCESS } = action.subtypes;

  // Check if this request is "cache-able"
  if (isFunction(selector)) {
    // Check if data is already present in the store
    const cache = yield select(selector);

    if ((cache && !isArray(cache)) || (isArray(cache) && cache.length)) {
      // Fake success action with the same data
      yield put({ type: SUCCESS, payload: cache, sagaPayload });

      return;
    }
  }

  try {
    // Resolve API request promise
    const [result]: [AxiosResponse] = yield all([
      promise(),
      delay(MINIMAL_API_PROMISE_RESOLVE_TIME),
    ]);

    // Store request result to the Redux
    yield put({ type: SUCCESS, payload: result.data, sagaPayload });

    // Do onSuccess callback if any
    if (isFunction(onSuccess)) {
      yield onSuccess(result);
    }
  } catch (error: GlobalAny) {
    if (axios.isCancel(error)) return;
    console.error(error);

    // Store request error to the Redux
    yield put({ type: FAIL, error, sagaPayload });

    const { response } = error;

    if (response) {
      switch (response.status) {
        case 401:
          BrowserStorageService.postAuthRedirect.set({
            pathname: window.location.pathname,
            search: window.location.pathname,
          });
          break;

        case 500:
          yield put(setGlobalError(response));
          break;

        default:
          try {
            if (isFunction(onFail)) onFail(response);
          } catch (onFailError: GlobalAny) {
            yield put(setGlobalError(onFailError));
          }
      }
    }
  }
}

export function* GlobalApiSaga() {
  yield takeEvery((action: AnyAction): boolean => {
    // Process actions with correct signature only
    const { promise, subtypes = {} } = action;
    const { SUCCESS, FAIL } = subtypes;

    return Boolean(promise && SUCCESS && FAIL);
  }, apiRequestHandler);
}
